import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Spinner from "../../components/Spinner";
import ErrorPage from "../../components/ErrorPage";
import http from "../../api/apiClient";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../AuthProvider";
import Modal from "../../components/Modal";
import {
  FaBan,
  FaCalendarAlt,
  FaChevronRight,
  FaClock,
  FaEdit,
  FaEnvelope,
  FaGlobe,
  FaInfoCircle,
  FaKey,
  FaLock,
  FaSave,
  FaSignOutAlt,
  FaTimes,
  FaTrash,
  FaUser,
  FaUserShield,
  FaUsersCog,
} from "react-icons/fa";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../components/lds/LdsTable";
import { UserHero, UserPageShell } from "./components";

import BackButton, { useLeaveForm } from "../../components/navigation/BackButton";
type UserRecord = Record<string, any> & {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled?: boolean;
};

type SessionRecord = Record<string, any>;

type PasswordModalState = {
  show: boolean;
  type: "success" | "error";
  title: string;
  message: string;
  copyableText?: string;
};

type UserDraft = {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  preferred_language: string;
};

const createUserDraft = (user?: UserRecord | null): UserDraft => ({
  firstName:
    user?.firstName ||
    user?.givenName ||
    user?.given_name ||
    user?.attributes?.firstName?.[0] ||
    user?.attributes?.given_name?.[0] ||
    user?.attributes?.first_name?.[0] ||
    "",
  lastName:
    user?.lastName ||
    user?.familyName ||
    user?.family_name ||
    user?.attributes?.lastName?.[0] ||
    user?.attributes?.family_name?.[0] ||
    user?.attributes?.last_name?.[0] ||
    "",
  username: user?.username || "",
  email: user?.email || "",
  preferred_language:
    user?.preferred_language ||
    user?.preferredLanguage ||
    user?.attributes?.preferred_language?.[0] ||
    user?.attributes?.locale?.[0] ||
    "",
});

const readBooleanLike = (value: unknown): boolean | null => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "enabled", "active"].includes(normalized)) return true;
    if (["false", "0", "no", "disabled", "inactive"].includes(normalized)) return false;
  }
  if (typeof value === "number") return value !== 0;
  return null;
};

const hasCredentialType = (value: unknown, types: string[]): boolean => {
  if (!Array.isArray(value)) return false;
  return value.some((entry) => {
    const candidate =
      typeof entry === "string"
        ? entry
        : entry && typeof entry === "object"
          ? (entry as Record<string, unknown>).type || (entry as Record<string, unknown>).credentialType
          : "";
    return types.includes(String(candidate).toLowerCase());
  });
};

const resolveMfaEnabled = (user?: UserRecord | null): boolean => {
  if (!user) return false;

  const explicitValues = [
    user.mfaEnabled,
    user.mfa_enabled,
    user.twoFactorEnabled,
    user.two_factor_enabled,
    user.totp,
    user.otpEnabled,
    user.otp_enabled,
    user.attributes?.mfaEnabled?.[0],
    user.attributes?.mfa_enabled?.[0],
    user.attributes?.twoFactorEnabled?.[0],
    user.attributes?.two_factor_enabled?.[0],
    user.attributes?.totp?.[0],
  ];
  for (const value of explicitValues) {
    const parsed = readBooleanLike(value);
    if (parsed !== null) return parsed;
  }

  const requiredActions = Array.isArray(user.requiredActions) ? user.requiredActions.map((action) => String(action).toUpperCase()) : [];
  if (requiredActions.includes("CONFIGURE_TOTP")) return false;

  const mfaCredentialTypes = ["otp", "totp", "webauthn", "webauthn-passwordless"];
  return (
    hasCredentialType(user.disableableCredentialTypes, mfaCredentialTypes) ||
    hasCredentialType(user.configuredUserStorageCredentialTypes, mfaCredentialTypes) ||
    hasCredentialType(user.credentials, mfaCredentialTypes)
  );
};

const UserDetails = () => {
  const { id } = useParams();
  const leaveForm = useLeaveForm();
  const { keycloak } = useContext(AuthContext);
  const { t, i18n } = useTranslation();

  const [user, setUser] = useState<UserRecord | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<UserDraft>(createUserDraft());
  const [roles, setRoles] = useState<string[]>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshingRoles, setRefreshingRoles] = useState(false);
  const [error, setError] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [passwordModal, setPasswordModal] = useState<PasswordModalState>({ show: false, type: "success", title: "", message: "", copyableText: "" });
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState<any>(null);
  const [sessionSearch, setSessionSearch] = useState("");
  const [sessionsActionLoading, setSessionsActionLoading] = useState(false);
  const sessionsFetchedRef = useRef(false);

  const hasFetchedRolesRef = useRef(false);

  const fetchDetails = useCallback(async () => {
    if (!id) return;
    const isInitialFetch = !hasFetchedRolesRef.current;
    if (isInitialFetch) {
      setLoading(true);
    } else {
      setRefreshingRoles(true);
    }
    try {
      const { data } = await http.get(`/admin/users/${id}`);
      setUser(data.user);
      setDraft(createUserDraft(data.user));
      setEditMode(false);
      setRoles(Array.isArray(data.roles) ? data.roles : []);
      setAvailableRoles(Array.isArray(data.availableRoles) ? data.availableRoles : []);
      setError(null);
      hasFetchedRolesRef.current = true;
    } catch (err) {
      setError(mapHttpErrorToMessage(err, t));
    } finally {
      if (isInitialFetch) {
        setLoading(false);
      } else {
        setRefreshingRoles(false);
      }
    }
  }, [id, t]);

  useEffect(() => {
    if (keycloak?.token) {
      fetchDetails();
    }
  }, [keycloak?.token, fetchDetails]);

  useEffect(() => {
    sessionsFetchedRef.current = false;
    setSessions([]);
    setSessionSearch("");
    setSessionsError(null);
  }, [id]);

  const fetchSessions = useCallback(
    async (withSpinner = true) => {
      if (!id) return;
      if (withSpinner) setSessionsLoading(true);
      setSessionsError(null);
      try {
        const { data } = await http.get(`/admin/users/${id}/sessions`);
        setSessions(Array.isArray(data) ? data : []);
      } catch (err) {
        setSessionsError(mapHttpErrorToMessage(err, t));
      } finally {
        if (withSpinner) setSessionsLoading(false);
      }
    },
    [id, t]
  );

  useEffect(() => {
    if (activeTab === "sessions" && !sessionsFetchedRef.current) {
      sessionsFetchedRef.current = true;
      fetchSessions(true);
    }
  }, [activeTab, fetchSessions]);

  const handleRetry = () => {
    setError(null);
    hasFetchedRolesRef.current = false;
    fetchDetails();
  };

  const handleAddRole = async () => {
    if (!selectedRole) return;
    try {
      setSaving(true);
      await http.post(`/admin/users/${id}/roles`, { role: selectedRole });
      setSelectedRole("");
      fetchDetails();
    } catch (err) {
      setError(mapHttpErrorToMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRole = async (role) => {
    try {
      setSaving(true);
      await http.delete(`/admin/users/${id}/roles/${encodeURIComponent(role)}`);
      fetchDetails();
    } catch (err) {
      setError(mapHttpErrorToMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      setSaving(true);
      const { data } = await http.post(`/admin/users/${id}/reset-password`);
      const tempPassword = data?.temporaryPassword || data?.password || "";
      setPasswordModal({
        show: true,
        type: "success",
        title: t("common.modal.success", { defaultValue: "Success" }),
        message: passwordResetSuccess,
        copyableText: tempPassword,
      });
    } catch (err) {
      setPasswordModal({
        show: true,
        type: "error",
        title: t("common.modal.error", { defaultValue: "Error" }),
        message: passwordResetFailed,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmToggleStatus = async () => {
    if (!user) return;
    try {
      setSaving(true);
      await http.patch(`/admin/users/${id}`, { enabled: !user.enabled });
      setUser((prev) => (prev ? { ...prev, enabled: !prev.enabled } : prev));
      setStatusModalOpen(false);
      setError(null);
    } catch (err) {
      setStatusModalOpen(false);
      setError(mapHttpErrorToMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancelEdit = () => {
    setDraft(createUserDraft(user));
    setEditMode(false);
  };

  const handleSaveUser = async () => {
    if (!user) return;
    try {
      setSaving(true);
      const payload = {
        firstName: draft.firstName,
        lastName: draft.lastName,
        username: draft.username,
        email: draft.email,
        preferred_language: draft.preferred_language || "nl",
      };
      await http.patch(`/admin/users/${id}`, payload);
      setUser((prev) => (prev ? { ...prev, ...payload } : prev));
      setEditMode(false);
      setError(null);
    } catch (err) {
      setError(mapHttpErrorToMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    const confirmLabel = t("admin.users.confirm_delete", {
      defaultValue: "Are you sure you want to delete this user?",
    });
    if (!window.confirm(confirmLabel)) return;
    try {
      setSaving(true);
      await http.delete(`/admin/users/${id}`);
      leaveForm("/admin/users");
    } catch (err) {
      setError(mapHttpErrorToMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  const { statusLabel, statusClasses } = useMemo(() => {
    if (!user) {
      return {
        statusLabel: "",
        statusClasses: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
      };
    }

    if (user.enabled) {
      return {
        statusLabel: t("common.badges.active", { defaultValue: "Active" }),
        statusClasses: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
      };
    }

    return {
      statusLabel: t("common.badges.inactive", { defaultValue: "Inactive" }),
      statusClasses: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    };
  }, [user, t]);

  const pageTitle = t("admin.users.details_title", { defaultValue: "User details" });
  const addRoleLabel = t("admin.users.add_role", { defaultValue: "Add" });
  const availableRolesLabel = t("admin.users.available_roles", { defaultValue: "Available roles" });
  const deleteLabel = t("common.actions.delete", { defaultValue: "Delete" });
  const editLabel = t("common.actions.edit", { defaultValue: "Edit" });
  const saveLabel = t("common.actions.save", { defaultValue: "Save" });
  const cancelLabel = t("common.actions.cancel", { defaultValue: "Cancel" });
  const editingLabel = t("common.actions.editing", { defaultValue: "Editing" });
  const rolesHeader = t("admin.users.roles", { defaultValue: "Roles" });
  const noRolesLabel = t("admin.users.no_roles", { defaultValue: "No roles" });
  const removeRoleLabel = t("admin.users.remove_role", { defaultValue: "Remove role" });
  const emailLabel = t("admin.users.email", { defaultValue: "Email" });
  const firstNameLabel = t("admin.users.first_name", { defaultValue: "First name" });
  const lastNameLabel = t("admin.users.last_name", { defaultValue: "Last name" });
  const usernameLabel = t("admin.users.username", { defaultValue: "Username" });
  const preferredLanguageLabel = t("admin.users.preferred_language", { defaultValue: "Preferred language" });
  const passwordLabel = t("admin.users.password", { defaultValue: "Password" });
  const statusLabelText = t("admin.users.status", { defaultValue: "Status" });
  const resetPasswordLabel = t("admin.users.reset_password", { defaultValue: "Reset password" });
  const infoTabLabel = t("admin.users.tabs.info", { defaultValue: "Account" });
  const rolesTabLabel = t("admin.users.tabs.roles", { defaultValue: "Roles" });
  const actionsTabLabel = t("admin.users.tabs.actions", { defaultValue: "Security" });
  const toggleStatusLabel = user?.enabled
    ? t("admin.users.deactivate_user", { defaultValue: "Deactivate user" })
    : t("admin.users.activate_user", { defaultValue: "Activate user" });
  const statusModalTitle = toggleStatusLabel;
  const statusModalMessage = user?.enabled
    ? t("admin.users.confirm_deactivate", {
        defaultValue: "Are you sure you want to deactivate this user? They will no longer be able to sign in.",
      })
    : t("admin.users.confirm_activate", {
        defaultValue: "Are you sure you want to activate this user? They will regain access.",
      });
  const passwordResetSuccess = t("admin.users.password_reset_success", { defaultValue: "Temporary password generated." });
  const passwordResetFailed = t("admin.users.password_reset_failed", { defaultValue: "Failed to reset password." });
  const actionLabels = {
    disable: t("admin.users.actions.labels.disable", { defaultValue: "Temporarily disable this account" }),
    activate: t("admin.users.actions.labels.activate", { defaultValue: "Reactivate this account" }),
    resetPassword: t("admin.users.actions.labels.reset_password", { defaultValue: "Send a new temporary password" }),
    delete: t("admin.users.actions.labels.delete", { defaultValue: "Delete this user permanently" }),
  };
  const actionHints = {
    disable: t("admin.users.actions.hints.disable", { defaultValue: "User cannot sign in until you activate them again." }),
    activate: t("admin.users.actions.hints.activate", { defaultValue: "Restore access so the user can sign in again." }),
    resetPassword: t("admin.users.actions.hints.reset_password", {
      defaultValue: "Generate a temporary password and share it securely with the user.",
    }),
    delete: t("admin.users.actions.hints.delete", { defaultValue: "Removes the user and their access permanently." }),
  };
  const toggleActionLabel = user?.enabled ? actionLabels.disable : actionLabels.activate;
  const toggleActionHint = user?.enabled ? actionHints.disable : actionHints.activate;

  const preferredLanguage =
    user?.preferred_language ||
    user?.preferredLanguage ||
    user?.attributes?.preferred_language?.[0] ||
    user?.attributes?.locale?.[0] ||
    "";
  const preferredLanguageDisplay = preferredLanguage || "—";
  const displayName = [draft.firstName, draft.lastName].filter(Boolean).join(" ").trim() || pageTitle;
  const sessionsTabLabel = t("admin.users.tabs.sessions", { defaultValue: "Sessions" });
  const sessionsTitle = t("admin.users.sessions.title", { defaultValue: "Active sessions" });
  const sessionsDescription = t("admin.users.sessions.description", {
    defaultValue: "View and manage the active sessions for this account.",
  });
  const sessionsSearchPlaceholder = t("admin.users.sessions.search_placeholder", {
    defaultValue: "Search by IP, client or session ID…",
  });
  const sessionsStartedLabel = t("admin.users.sessions.started", { defaultValue: "Started" });
  const sessionsLastAccessLabel = t("admin.users.sessions.last_access", { defaultValue: "Last access" });
  const sessionsClientLabel = t("admin.users.sessions.client", { defaultValue: "Client" });
  const sessionsIpLabel = t("admin.users.sessions.ip", { defaultValue: "IP address" });
  const sessionsEmptyLabel = t("admin.users.sessions.empty", { defaultValue: "No active sessions." });
  const sessionsLoadingLabel = t("admin.users.sessions.loading", { defaultValue: "Loading sessions…" });
  const sessionsLogoutAllLabel = t("admin.users.sessions.logout_all", { defaultValue: "Logout all sessions" });
  const sessionsRefreshLabel = t("admin.users.sessions.refresh", { defaultValue: "Refresh" });
  const sessionsConfirmLogout = t("admin.users.sessions.confirm_logout", {
    defaultValue: "Are you sure you want to log out all sessions for this user?",
  });
  const tabs = [
    { id: "info", label: infoTabLabel },
    { id: "roles", label: rolesTabLabel },
    { id: "sessions", label: sessionsTabLabel },
    { id: "actions", label: actionsTabLabel },
  ];

  const filteredSessions = useMemo(() => {
    if (!sessionSearch) return sessions;
    const term = sessionSearch.toLowerCase();
    return sessions.filter((sessionItem) =>
      [sessionItem?.ipAddress, sessionItem?.client, sessionItem?.sessionId, sessionItem?.started, sessionItem?.lastAccess]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term))
    );
  }, [sessionSearch, sessions]);

  const formatDateTime = useCallback(
    (value) => {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      try {
        return date.toLocaleString(i18n.language || undefined, {
          dateStyle: "medium",
          timeStyle: "medium",
        });
      } catch (e) {
        return date.toLocaleString();
      }
    },
    [i18n.language]
  );

  const handleRefreshSessions = () => {
    fetchSessions(true);
  };

  const handleLogoutSessions = async () => {
  if (!sessions.length) return;
  if (!window.confirm(sessionsConfirmLogout)) return;
  setSessionsActionLoading(true);
    setSessionsError(null);
    try {
      await http.post(`/admin/users/${id}/sessions/logout`);
      await fetchSessions(false);
    } catch (err) {
      setSessionsError(mapHttpErrorToMessage(err, t));
    } finally {
      setSessionsActionLoading(false);
    }
  };

  const inputClass =
    "mt-2 w-full rounded-2xl border-2 border-indigo-200 bg-white px-4 py-3 text-sm font-semibold text-slate-950 shadow-sm transition focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/15 dark:border-indigo-400/40 dark:bg-slate-950 dark:text-white";
  const labelClass = "text-xs font-bold uppercase tracking-wide text-indigo-600 dark:text-indigo-200";

  const rawLastAccess =
    user?.lastAccess ??
    user?.lastAccessAt ??
    user?.lastAccessed ??
    user?.lastLogin ??
    user?.lastLoginTimestamp ??
    user?.attributes?.lastAccess?.[0] ??
    user?.attributes?.last_access?.[0];
  const lastAccessDisplay = formatDateTime(rawLastAccess);
  const createdDisplay = formatDateTime(user?.createdTimestamp ?? user?.createdAt ?? user?.created);
  const updatedDisplay = formatDateTime(user?.updatedTimestamp ?? user?.updatedAt ?? user?.updated);
  const mfaEnabled = resolveMfaEnabled(user);
  const mfaLabel = mfaEnabled
    ? t("common.badges.enabled", { defaultValue: "Enabled" })
    : t("common.badges.disabled", { defaultValue: "Disabled" });
  const mfaDescription = mfaEnabled
    ? t("admin.users.mfa_active", { defaultValue: "Multi-factor authentication is active." })
    : t("admin.users.mfa_inactive", { defaultValue: "Multi-factor authentication is not active." });

  const accountInfoRows = [
    { key: "username", icon: FaUser, label: usernameLabel, value: user?.username || "—", action: editLabel },
    { key: "email", icon: FaEnvelope, label: emailLabel, value: user?.email || "—", action: editLabel },
    { key: "language", icon: FaGlobe, label: preferredLanguageLabel, value: preferredLanguageDisplay, action: editLabel },
    { key: "password", icon: FaLock, label: passwordLabel, value: t("admin.users.password_last_reset_unknown", { defaultValue: "Last reset unknown" }), action: resetPasswordLabel },
    { key: "identity", icon: FaKey, label: t("admin.users.keycloak_identity", { defaultValue: "Keycloak identity" }), value: t("admin.users.keycloak_connected", { defaultValue: "Connected" }), action: null },
    { key: "created", icon: FaCalendarAlt, label: t("admin.users.created", { defaultValue: "Created" }), value: createdDisplay, action: null },
    { key: "updated", icon: FaClock, label: t("admin.users.updated", { defaultValue: "Last updated" }), value: updatedDisplay, action: null },
  ];

  const quickActions = [
    {
      key: "roles",
      icon: FaUsersCog,
      label: t("admin.users.manage_roles", { defaultValue: "Manage roles" }),
      onClick: () => setActiveTab("roles"),
      disabled: false,
      tone: "indigo",
    },
    {
      key: "reset",
      icon: FaKey,
      label: resetPasswordLabel,
      onClick: handleResetPassword,
      disabled: saving,
      tone: "indigo",
    },
    {
      key: "logout",
      icon: FaSignOutAlt,
      label: sessionsLogoutAllLabel,
      onClick: handleLogoutSessions,
      disabled: sessionsActionLoading || !sessions.length,
      tone: "indigo",
    },
    {
      key: "status",
      icon: FaBan,
      label: toggleStatusLabel,
      onClick: () => setStatusModalOpen(true),
      disabled: saving,
      tone: "red",
    },
    {
      key: "delete",
      icon: FaTrash,
      label: deleteLabel,
      onClick: handleDeleteUser,
      disabled: saving,
      tone: "red",
    },
  ];

  if (loading) return <Spinner />;
  if (error) {
    return <ErrorPage message={error} status={error?.status} onRetry={handleRetry} />;
  }

  return (
    <UserPageShell>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <BackButton fallback="/admin/users" />
      </div>
      <UserHero
        icon={FaUserShield}
        eyebrow={t("admin.users.management_eyebrow", { defaultValue: "User management" })}
        title={displayName}
        description={user.email || "—"}
        actions={
          <>
            <span className={`inline-flex items-center rounded-full px-4 py-2 text-xs font-semibold ${statusClasses}`}>
              {statusLabelText}: {statusLabel}
            </span>
            {editMode && (
              <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white px-4 py-2 text-xs font-bold uppercase tracking-wide text-indigo-700 shadow-sm dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200">
                {editingLabel}
              </span>
            )}
            {editMode ? (
              <>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:text-white"
                >
                  <FaTimes aria-hidden="true" />
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  onClick={handleSaveUser}
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-5 py-2 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20"
                >
                  <FaSave aria-hidden="true" />
                  {saving ? t("common.loading", { defaultValue: "Loading…" }) : saveLabel}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setActiveTab("info");
                  setEditMode(true);
                }}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-5 py-2 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20"
              >
                <FaEdit aria-hidden="true" />
                {editLabel}
              </button>
            )}
          </>
        }
      />

      <div className="flex flex-col gap-6 text-slate-950 dark:text-white">
        <section className="lds-section-enter">
          <div className="flex flex-wrap gap-8 border-b border-slate-200 dark:border-white/10">
            {tabs.map((tab) => {
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative px-3 py-4 text-sm font-semibold transition ${
                    isActive
                      ? "text-indigo-700 dark:text-indigo-200"
                      : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                  }`}
                >
                  {tab.label}
                  {isActive && (
                    <span className="absolute inset-x-0 bottom-[-1px] h-0.5 rounded-full bg-indigo-600 dark:bg-indigo-300" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-6 space-y-6">
            {activeTab === "info" && (
              editMode ? (
                <div className="space-y-5 rounded-[22px] border-2 border-indigo-200 bg-indigo-50/55 p-4 shadow-[0_16px_45px_rgba(67,56,202,0.12)] dark:border-indigo-400/35 dark:bg-indigo-400/10 sm:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-indigo-600 dark:text-indigo-200">
                        {editingLabel}
                      </p>
                      <h3 className="text-lg font-semibold text-slate-950 dark:text-white">
                        {t("admin.users.edit_section", { defaultValue: "Edit user details" })}
                      </h3>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        disabled={saving}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:text-white"
                      >
                        <FaTimes aria-hidden="true" />
                        {cancelLabel}
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveUser}
                        disabled={saving}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200 bg-white px-5 py-2 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20"
                      >
                        <FaSave aria-hidden="true" />
                        {saving ? t("common.loading", { defaultValue: "Loading…" }) : saveLabel}
                      </button>
                    </div>
                  </div>
                  <div className="grid gap-5 text-sm sm:grid-cols-2 lg:grid-cols-3">
                    <div className="rounded-[18px] border border-indigo-200 bg-white px-5 py-4 shadow-sm dark:border-indigo-400/30 dark:bg-slate-950/80">
                      <label className={labelClass} htmlFor="firstName">{firstNameLabel}</label>
                      <input id="firstName" name="firstName" value={draft.firstName} onChange={handleEditChange} className={inputClass} />
                    </div>
                    <div className="rounded-[18px] border border-indigo-200 bg-white px-5 py-4 shadow-sm dark:border-indigo-400/30 dark:bg-slate-950/80">
                      <label className={labelClass} htmlFor="lastName">{lastNameLabel}</label>
                      <input id="lastName" name="lastName" value={draft.lastName} onChange={handleEditChange} className={inputClass} />
                    </div>
                    <div className="rounded-[18px] border border-indigo-200 bg-white px-5 py-4 shadow-sm dark:border-indigo-400/30 dark:bg-slate-950/80">
                      <label className={labelClass} htmlFor="username">{usernameLabel}</label>
                      <input id="username" name="username" value={draft.username} onChange={handleEditChange} className={inputClass} />
                    </div>
                    <div className="rounded-[18px] border border-indigo-200 bg-white px-5 py-4 shadow-sm dark:border-indigo-400/30 dark:bg-slate-950/80">
                      <label className={labelClass} htmlFor="email">{emailLabel}</label>
                      <input id="email" name="email" type="email" value={draft.email} onChange={handleEditChange} className={inputClass} />
                    </div>
                    <div className="rounded-[18px] border border-indigo-200 bg-white px-5 py-4 shadow-sm dark:border-indigo-400/30 dark:bg-slate-950/80">
                      <label className={labelClass} htmlFor="preferred_language">{preferredLanguageLabel}</label>
                      <input id="preferred_language" name="preferred_language" value={draft.preferred_language} onChange={handleEditChange} className={inputClass} />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid gap-5 xl:grid-cols-[minmax(0,1.5fr)_minmax(280px,0.7fr)_minmax(280px,0.8fr)]">
                  <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
                    <h2 className="text-lg font-semibold text-slate-950 dark:text-white">
                      {t("admin.users.account_information", { defaultValue: "Account information" })}
                    </h2>
                    <div className="mt-5 divide-y divide-slate-200 dark:divide-white/10">
                      {accountInfoRows.map(({ key, icon: Icon, label, value, action }) => (
                        <div key={key} className="grid gap-4 py-3 sm:grid-cols-[48px_minmax(120px,0.7fr)_minmax(0,1fr)_auto] sm:items-center">
                          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-200">
                            <Icon aria-hidden="true" />
                          </span>
                          <span className="font-medium text-slate-700 dark:text-slate-300">{label}</span>
                          <span className="break-words font-semibold text-slate-950 dark:text-white">{value}</span>
                          {action ? (
                            <button
                              type="button"
                              onClick={key === "password" ? handleResetPassword : () => setEditMode(true)}
                              disabled={saving}
                              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-indigo-200 dark:hover:bg-indigo-400/10"
                            >
                              {action}
                            </button>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
                      <h2 className="text-lg font-semibold text-slate-950 dark:text-white">
                        {t("admin.users.account_status", { defaultValue: "Account status" })}
                      </h2>
                      <div className="mt-5">
                        <span className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold ${statusClasses}`}>
                          <span className="h-2 w-2 rounded-full bg-current" />
                          {statusLabel}
                        </span>
                        <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                          {user?.enabled
                            ? t("admin.users.account_can_login", { defaultValue: "The user can log in and use the system." })
                            : t("admin.users.account_cannot_login", { defaultValue: "The user cannot currently log in." })}
                        </p>
                      </div>
                      <div className="mt-6 border-t border-slate-200 pt-5 dark:border-white/10">
                        <div className="flex items-center justify-between gap-3 text-sm">
                          <span className="font-semibold text-slate-950 dark:text-white">MFA</span>
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-bold ${
                              mfaEnabled
                                ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-200"
                                : "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300"
                            }`}
                          >
                            {mfaLabel}
                          </span>
                        </div>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{mfaDescription}</p>
                        <div className="mt-4 flex items-center justify-between gap-3 text-sm">
                          <span className="font-semibold text-slate-950 dark:text-white">{sessionsTitle}</span>
                          <span className="rounded-lg bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-200">
                            {sessions.length}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
                      <h2 className="text-lg font-semibold text-slate-950 dark:text-white">
                        {t("admin.users.login_summary", { defaultValue: "Login summary" })}
                      </h2>
                      <div className="mt-5 space-y-3 text-sm">
                        <div className="grid grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-3">
                          <FaCalendarAlt aria-hidden="true" className="text-slate-500 dark:text-slate-400" />
                          <span className="text-slate-500 dark:text-slate-400">{t("admin.users.last_login", { defaultValue: "Last login" })}</span>
                          <span className="font-semibold text-slate-950 dark:text-white">{lastAccessDisplay}</span>
                        </div>
                        <div className="grid grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-3">
                          <FaClock aria-hidden="true" className="text-slate-500 dark:text-slate-400" />
                          <span className="text-slate-500 dark:text-slate-400">{t("admin.users.last_login_ip", { defaultValue: "Last login IP" })}</span>
                          <span className="font-semibold text-slate-950 dark:text-white">{user?.lastLoginIp || user?.ipAddress || "—"}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
                    <h2 className="text-lg font-semibold text-slate-950 dark:text-white">
                      {t("admin.users.quick_actions", { defaultValue: "Quick actions" })}
                    </h2>
                    <div className="mt-5 space-y-3">
                      {quickActions.map(({ key, icon: Icon, label, onClick, disabled, tone }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={onClick}
                          disabled={disabled}
                          className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-semibold text-slate-800 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-100 dark:hover:bg-indigo-400/10"
                        >
                          <span
                            className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                              tone === "red"
                                ? "bg-red-50 text-red-600 dark:bg-red-400/10 dark:text-red-200"
                                : "bg-indigo-50 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-200"
                            }`}
                          >
                            <Icon aria-hidden="true" />
                          </span>
                          <span className="min-w-0 flex-1">{label}</span>
                          <FaChevronRight aria-hidden="true" className="text-slate-400" />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-indigo-100 bg-indigo-50/60 px-5 py-4 text-sm text-slate-600 dark:border-indigo-400/20 dark:bg-indigo-400/10 dark:text-slate-300 xl:col-span-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700 dark:bg-indigo-400/15 dark:text-indigo-200">
                        <FaInfoCircle aria-hidden="true" />
                      </span>
                      <span>
                        {t("admin.users.access_change_notice", {
                          defaultValue: "Changes to roles, security settings or account status may affect the user's access.",
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              )
            )}

            {activeTab === "roles" && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-indigo-100/70 bg-gradient-to-br from-white via-indigo-50/75 to-white p-4 shadow-[0_15px_35px_rgba(15,23,42,0.08)] dark:border-slate-700 dark:bg-gradient-to-br dark:from-slate-950/85 dark:via-slate-900/65 dark:to-slate-950/90 dark:shadow-[0_20px_50px_rgba(0,0,0,0.55)]">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-indigo-100">{rolesHeader}</h3>
                    <p className="text-xs text-gray-500 dark:text-indigo-200/70">
                      {t("admin.users.role_hint", { defaultValue: "Assign or remove roles" })}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400" htmlFor="role-select">
                      {availableRolesLabel}
                    </label>
                    <select
                      id="role-select"
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="rounded-full border border-gray-200/70 bg-white/80 px-4 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-100"
                    >
                      <option value="">—</option>
                      {availableRoles.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleAddRole}
                      disabled={!selectedRole || saving}
                      className="lumen-glow-sheen inline-flex items-center rounded-full border border-indigo-200/70 bg-indigo-50/80 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200 disabled:opacity-60"
                    >
                      {addRoleLabel}
                    </button>
                  </div>
                </div>

                <LdsTableCard className="overflow-hidden">
                  <LdsTableScroll>
                    <LdsTable>
                      <LdsTableHead>
                        <LdsTableRow>
                          <LdsTableHeadCell>{rolesHeader}</LdsTableHeadCell>
                          <LdsTableHeadCell className="text-right">
                            {t("common.actions.name", { defaultValue: "Actions" })}
                          </LdsTableHeadCell>
                        </LdsTableRow>
                      </LdsTableHead>
                      <LdsTableBody>
                      {roles.length === 0 ? (
                        <LdsTableRow>
                          <LdsTableCell colSpan={2} className="text-slate-500 dark:text-slate-400">
                            {noRolesLabel}
                          </LdsTableCell>
                        </LdsTableRow>
                      ) : (
                        roles.map((role) => (
                          <LdsTableRow key={role}>
                            <LdsTableCell className="font-semibold text-slate-950 dark:text-white">
                              {role}
                            </LdsTableCell>
                            <LdsTableCell className="text-right">
                              <button
                                type="button"
                                onClick={() => handleRemoveRole(role)}
                                disabled={saving}
                                className="lumen-glow-sheen inline-flex items-center rounded-full border border-red-200/70 bg-red-50/80 px-4 py-1 text-xs font-semibold text-red-700 transition hover:text-red-900 dark:border-red-500/40 dark:bg-red-900/30 dark:text-red-200 disabled:opacity-60"
                              >
                                {removeRoleLabel}
                              </button>
                            </LdsTableCell>
                          </LdsTableRow>
                        ))
                      )}
                      </LdsTableBody>
                    </LdsTable>
                  </LdsTableScroll>
                </LdsTableCard>
              </div>
            )}

            {activeTab === "sessions" && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">{sessionsTitle}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{sessionsDescription}</p>
                </div>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <label htmlFor="session-search" className="sr-only">
                      {sessionsSearchPlaceholder}
                    </label>
                    <input
                      id="session-search"
                      type="search"
                      value={sessionSearch}
                      onChange={(e) => setSessionSearch(e.target.value)}
                      placeholder={sessionsSearchPlaceholder}
                      className="w-full rounded-2xl border border-gray-200/70 bg-white/80 px-4 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-100"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleRefreshSessions}
                      disabled={sessionsLoading || sessionsActionLoading}
                      className="lumen-glow-sheen inline-flex items-center rounded-full border border-gray-200/70 bg-white/80 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:text-gray-900 dark:border-gray-600/40 dark:bg-gray-900/40 dark:text-gray-200 disabled:opacity-60"
                    >
                      {sessionsRefreshLabel}
                    </button>
                    <button
                      type="button"
                      onClick={handleLogoutSessions}
                      disabled={sessionsActionLoading || !sessions.length}
                      className="lumen-glow-sheen inline-flex items-center rounded-full border border-red-200/70 bg-red-50/80 px-4 py-2 text-sm font-semibold text-red-700 transition hover:text-red-900 dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-200 disabled:opacity-60"
                    >
                      {sessionsLogoutAllLabel}
                    </button>
                  </div>
                </div>
                {sessionsError && (
                  <div className="rounded-2xl border border-red-500/60 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-900/30 dark:text-red-200">
                    {sessionsError.message || sessionsError.title || sessionsError}
                  </div>
                )}
                <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700">
                  {sessionsLoading ? (
                    <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-300">{sessionsLoadingLabel}</div>
                  ) : filteredSessions.length ? (
                    <table className="min-w-full border-collapse text-sm">
                      <thead className="indigo-table-head text-xs font-semibold uppercase tracking-wide">
                        <tr>
                          <th className="px-4 py-3">{sessionsStartedLabel}</th>
                          <th className="px-4 py-3">{sessionsLastAccessLabel}</th>
                          <th className="px-4 py-3">{sessionsClientLabel}</th>
                          <th className="px-4 py-3">{sessionsIpLabel}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white/90 dark:divide-gray-700 dark:bg-gray-900/30">
                        {filteredSessions.map((sessionItem, idx) => {
                          const startedLabel = formatDateTime(sessionItem?.started || sessionItem?.start || sessionItem?.startTime);
                          const lastAccessLabel = formatDateTime(sessionItem?.lastAccess || sessionItem?.last_access);
                          const clientLabel = sessionItem?.client || sessionItem?.clientId || sessionItem?.clientName || "—";
                          const ipLabel = sessionItem?.ipAddress || sessionItem?.ip || "—";
                          const rowKey = sessionItem?.sessionId || sessionItem?.id || `${sessionItem?.started || ""}-${idx}`;
                          return (
                            <tr key={rowKey}>
                              <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:text-gray-100">{startedLabel}</td>
                              <td className="whitespace-nowrap px-4 py-2 text-gray-900 dark:text-gray-100">{lastAccessLabel}</td>
                              <td className="px-4 py-2 text-gray-700 dark:text-gray-200">{clientLabel}</td>
                              <td className="px-4 py-2 text-gray-700 dark:text-gray-200">{ipLabel}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-300">{sessionsEmptyLabel}</div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "actions" && (
              <div className="space-y-4">
                {[
                  {
                    label: toggleActionLabel,
                    hint: toggleActionHint,
                    action: () => setStatusModalOpen(true),
                    button: toggleStatusLabel,
                    className:
                      "lumen-glow-sheen inline-flex items-center rounded-full border border-red-200/70 bg-red-50/80 px-4 py-2 text-sm font-semibold text-red-700 transition hover:text-red-900 dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-200",
                  },
                  {
                    label: actionLabels.resetPassword,
                    hint: actionHints.resetPassword,
                    action: handleResetPassword,
                    button: resetPasswordLabel,
                    className:
                      "lumen-glow-sheen inline-flex items-center rounded-full border border-indigo-200/70 bg-indigo-50/80 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200",
                  },
                  {
                    label: actionLabels.delete,
                    hint: actionHints.delete,
                    action: handleDeleteUser,
                    button: deleteLabel,
                    className:
                      "lumen-glow-sheen inline-flex items-center rounded-full border border-red-200/70 bg-red-50/80 px-4 py-2 text-sm font-semibold text-red-700 transition hover:text-red-900 dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-200",
                  },
                ].map(({ label, hint, action, button, className }) => (
                  <div
                    key={label}
                    className="flex flex-col gap-3 rounded-[18px] border border-indigo-100/70 bg-gradient-to-br from-white via-white to-indigo-50/70 px-5 py-4 shadow-sm dark:border-slate-700 dark:bg-gradient-to-br dark:from-slate-950/85 dark:via-slate-900/70 dark:to-slate-950/85 dark:shadow-[0_20px_50px_rgba(0,0,0,0.55)] sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="font-semibold text-gray-700 dark:text-indigo-100">{label}</p>
                      <p className="text-xs text-gray-500 dark:text-indigo-200/70">{hint}</p>
                    </div>
                    <button type="button" onClick={action} disabled={saving} className={`${className} w-full sm:w-auto`}>
                      {button}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <BackButton fallback="/admin/users" />
        </div>
        </section>

       
      </div>
      <Modal
        show={statusModalOpen}
        type="confirm"
        title={statusModalTitle}
        message={statusModalMessage}
        onConfirm={handleConfirmToggleStatus}
        onClose={() => setStatusModalOpen(false)}
      />
      <Modal
        show={passwordModal.show}
        type={passwordModal.type}
        title={passwordModal.title}
        message={passwordModal.message}
        copyableText={passwordModal.copyableText}
        onClose={() => setPasswordModal({ show: false, type: "success", title: "", message: "", copyableText: "" })}
      />
    </UserPageShell>
  );
};

export default UserDetails;
